
import { Component, Prop, Vue } from 'vue-property-decorator';

import {
  BaseText,
  HeadingDisplay,
  ButtonIconContextual,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { ErrorHandler } from '@/modules/common/components/ErrorHandler';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import CashBalanceLoader from '@/modules/cash/components/CashBalanceLoader/CashBalanceLoader.vue';
import HomeCashActions from './components/HomeCashActions.vue';
import { HomeCashDepositModal } from './components/HomeCashDepositModal';
import { HomeCashDepositModalV2 } from './components/HomeCashDepositModalV2';
import { CashBalance } from '@/modules/cash/components/CashBalance';
import { Money } from '@/modules/common/components/Money';
import { TransitModal } from '@/modules/common/components/TransitModal';

import {
  Cash,
  CashBankAccount,
  HomeBankAccountDocument,
  HomeBankAccountQuery,
  HomeCashDocument,
  HomeCashQuery
} from '@/modules/common/services/graphql/server/server.sdk';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  trackReloadBalanceAccountErrorClick,
  trackBalanceAccountErrorLoad
} from './track-events';
import { trackTransactionsPageNavigation } from '@/modules/transaction/helpers/transactions-events';
import { WarrenModules } from '@/types/navigation';
import * as trackEvents from './track-events';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import {
  getCashSummary,
  CashSummaryResponse
} from '@/modules/home/services/bff';

@Component({
  components: {
    FadeTransition,
    HomeCashActions,
    HomeCashDepositModal,
    HomeCashDepositModalV2,
    CashBalanceLoader,
    ErrorHandler,
    CashBalance,
    BaseText,
    HeadingDisplay,
    ButtonIconContextual,
    Money,
    TransitModal,
    SkeletonLoader
  }
})
export default class HomeCash extends Vue {
  readonly NebraskaColors = NebraskaColors;

  @Prop({ type: Boolean, required: true })
  readonly isOverdue!: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  readonly showHomeCashDepositModal!: boolean;

  oldCashData: Reactive<Cash> = null;
  newCashData: Reactive<CashSummaryResponse> = null;
  bankAccount: Reactive<CashBankAccount> = null;
  isLoading = false;
  hasError = false;
  showBalanceDetail = false;
  isTransitModalOpen = false;

  get isUnifiedAccountEnabled() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.UNIFIED_ACCOUNT);
  }

  get isAccountProcessingWarningEnabled() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ACCOUNT_PROCESSING_WARNING
    );
  }

  get isTransitAmountEnabled() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_TRANSIT_AMOUNT);
  }

  get isDepositPixEnabled() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_DEPOSIT_PIX);
  }

  created() {
    this.loadCash();
  }

  reloadCash() {
    this.loadCash();
    trackReloadBalanceAccountErrorClick();
  }

  async loadCash() {
    this.isUnifiedAccountEnabled || this.isAccountProcessingWarningEnabled
      ? await this.fetchNewCash()
      : await this.fetchOldCash();
  }

  async fetchOldCash() {
    this.isLoading = true;
    this.hasError = false;

    try {
      const { data } = await this.$apollo.query<HomeCashQuery>({
        query: HomeCashDocument
      });
      this.oldCashData = data.cash as Cash;
      this.bankAccount = data.cash?.bankAccount!;
    } catch {
      this.hasError = true;
      trackBalanceAccountErrorLoad();
    } finally {
      this.isLoading = false;
    }
  }

  async fetchNewCash() {
    this.isLoading = true;
    this.hasError = false;

    try {
      this.newCashData = await getCashSummary();
      const { data } = await this.$apollo.query<HomeBankAccountQuery>({
        query: HomeBankAccountDocument
      });
      this.bankAccount = data.cash?.bankAccount!;
    } catch {
      this.hasError = true;
      trackBalanceAccountErrorLoad();
    } finally {
      this.isLoading = false;
    }
  }

  get balance() {
    if (this.isUnifiedAccountEnabled || this.isAccountProcessingWarningEnabled)
      return this.isUnifiedAccountEnabled
        ? this.cashSectionSummary.accountAmount
        : this.cashSectionSummary.netBalance;
    return this.oldCashData?.summary?.netBalance?.formatted || '';
  }

  get cashSectionSummary() {
    return {
      netBalance: this.getValueWithDefault(
        this.newCashData?.cashSection.summary?.netBalance?.formatted,
        ''
      ),
      accountAmount: this.getValueWithDefault(
        this.newCashData?.cashSection.summary?.accountAmount?.formatted,
        ''
      ),
      totalTransitAmount: this.getValueWithDefault(
        this.newCashData?.cashSection.summary?.totalTransitAmount?.amount
          ?.formatted
      ),
      wealthProjectedAmount: this.getValueWithDefault(
        this.newCashData?.cashSection.summary?.totalTransitAmount?.details
          ?.wealth?.projectedCreditAmount?.formatted
      ),
      tradeTotalValue: this.getValueWithDefault(
        this.newCashData?.cashSection.summary?.totalTransitAmount?.details
          ?.trade?.totalValue?.formatted
      ),
      tradeValues:
        this.newCashData?.cashSection.summary?.totalTransitAmount?.details?.trade?.items?.map(
          item => {
            return {
              label: item.label,
              value: item.value.formatted
            };
          }
        ) || [],
      isNegativeTransitAmount:
        this.newCashData?.cashSection.summary?.totalTransitAmount?.amount
          ?.value! < 0 || false
    };
  }

  getValueWithDefault(value?: string | null, defaultValue = 'R$ 0,00') {
    if (value) return value;
    return defaultValue;
  }

  get hasPositiveBalance() {
    if (this.isUnifiedAccountEnabled || this.isAccountProcessingWarningEnabled)
      return (
        this.newCashData?.cashSection.summary?.netBalance?.value &&
        this.newCashData.cashSection.summary.netBalance.value > 0
      );
    return this.oldCashData?.summary?.netBalance?.value > 0;
  }

  get hasActiveAccount(): boolean {
    if (this.isUnifiedAccountEnabled || this.isAccountProcessingWarningEnabled)
      return Boolean(
        this.newCashData?.cashSection.status?.hasInvestmentAccountEnabled
      );
    return Boolean(this.oldCashData?.status?.enabled);
  }

  get processingWarningText() {
    return this.newCashData?.cashSection.status?.processingWarning;
  }

  get cashAccountId() {
    if (this.isUnifiedAccountEnabled || this.isAccountProcessingWarningEnabled)
      return this.newCashData?.cashSection.status?.investmentAccountId ?? '';
    return this.oldCashData?.status?.investmentAccountId ?? '';
  }

  public openHomeCashDepositModal() {
    this.$emit('open-home-cash-deposit-modal');
  }

  public closeHomeCashDepositModal() {
    this.$emit('close-home-cash-deposit-modal');
  }

  public redirectToTransactionsPage() {
    this.$router.push({ name: 'transactions' });

    trackTransactionsPageNavigation(WarrenModules.Home);
    trackEvents.trackViewMoreTransactionsEvent();
  }

  handleTransitModal() {
    this.isTransitModalOpen = !this.isTransitModalOpen;
  }
}
